import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { policiesAPI, usersAPI, devicesAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarDaysIcon,
  ClockIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  BellIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';

function Settings() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  // All hooks must be called before any conditional logic
  const [activeTab, setActiveTab] = useState('policies');
  const [workingPolicies, setWorkingPolicies] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [attendanceAccess, setAttendanceAccess] = useState({
    allowWebAttendance: false
  });
  const [attendanceAccessSaving, setAttendanceAccessSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  // Form states
  const [policyForm, setPolicyForm] = useState({
    policyName: '',
    requiredDailyHours: 8,
    workDays: []
  });

  const [holidayForm, setHolidayForm] = useState({
    holidayName: '',
    holidayDate: '',
    applicableTo: 'Both'
  });

  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    clickupTeamId: '',
    clickupListId: '',
    orbitId: '',
    description: '',
    leaveTypeLimits: {},
    checkoutRequestLimit: '',
    shouldBlockOverLimit: false,
    overLimitNotice: ''
  });

  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: ''
  });

  // Confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning'
  });

  const tabs = [
    { id: 'policies', name: 'Working Hours Policies', icon: ClockIcon },
    { id: 'holidays', name: 'Holidays', icon: CalendarDaysIcon },
    { id: 'departments', name: 'Departments', icon: BuildingOfficeIcon },
    { id: 'attendance-access', name: 'Attendance Access', icon: ClockIcon },
    { id: 'push-notifications', name: 'Push Notifications', icon: BellIcon },
    { id: 'webhook', name: 'Webhook Settings', icon: GlobeAltIcon },
  ];

  const weekDays = [
    { id: 'monday', name: 'Monday' },
    { id: 'tuesday', name: 'Tuesday' },
    { id: 'wednesday', name: 'Wednesday' },
    { id: 'thursday', name: 'Thursday' },
    { id: 'friday', name: 'Friday' },
    { id: 'saturday', name: 'Saturday' },
    { id: 'sunday', name: 'Sunday' }
  ];

  const leaveLimitTypes = [
    'Casual Leave',
    'Sick Leave',
    'Earned Leave',
    'Emergency Leave',
    'Work from Home',
    'Half Day',
    'Short Leave'
  ];

  const buildLeaveLimitForm = (limits = {}) => leaveLimitTypes.reduce((acc, leaveType) => {
    const value = limits?.[leaveType];
    acc[leaveType] = value === undefined || value === null ? '' : String(value);
    return acc;
  }, {});

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'policies') {
        const response = await policiesAPI.getWorkingHoursPolicies();
        setWorkingPolicies(response.data || []);
      } else if (activeTab === 'holidays') {
        const response = await policiesAPI.getHolidays();
        setHolidays(response.data || []);
      } else if (activeTab === 'departments') {
        const response = await usersAPI.getDepartmentsWithCount();
        // Handle both direct array response and wrapped response
        const departmentsData = response.data?.departments || response.data || [];
        setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
      } else if (activeTab === 'attendance-access') {
        const response = await settingsAPI.getAttendanceAccess();
        setAttendanceAccess({
          allowWebAttendance: Boolean(response.data?.allowWebAttendance)
        });
      } else if (activeTab === 'push-notifications') {
        try {
          console.log('Loading devices...');
          const response = await devicesAPI.getAllDevices();
          console.log('Devices response:', response.data);
          const devicesData = response.data?.devices || [];
          console.log(`Loaded ${devicesData.length} device(s)`);
          setDevices(devicesData);
        } catch (deviceErr) {
          console.error('Error loading devices:', deviceErr);
          const errorMsg = deviceErr.response?.data?.error || deviceErr.message || 'Failed to load devices';
          toast.error(`Failed to load devices: ${errorMsg}`);
          setDevices([]);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      const errorMsg = err.response?.data?.error || err.message || 'An error occurred';
      setError(errorMsg);
      // Set empty arrays to prevent map errors
      if (activeTab === 'departments') {
        setDepartments([]);
      } else if (activeTab === 'attendance-access') {
        setAttendanceAccess({ allowWebAttendance: false });
      } else if (activeTab === 'policies') {
        setWorkingPolicies([]);
      } else if (activeTab === 'holidays') {
        setHolidays([]);
      } else if (activeTab === 'push-notifications') {
        setDevices([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAttendanceAccess = async () => {
    try {
      setAttendanceAccessSaving(true);
      const response = await settingsAPI.updateAttendanceAccess({
        allowWebAttendance: attendanceAccess.allowWebAttendance
      });
      setAttendanceAccess({
        allowWebAttendance: Boolean(response.data?.allowWebAttendance)
      });
      toast.success('Attendance access settings saved successfully!');
    } catch (err) {
      toast.error(`Failed to save attendance access settings: ${err.response?.data?.error || err.message}`);
    } finally {
      setAttendanceAccessSaving(false);
    }
  };

  // Working Hours Policy Functions
  const handleCreatePolicy = () => {
    setPolicyForm({
      policyName: '',
      requiredDailyHours: 8,
      workDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    });
    setSelectedPolicy(null);
    setIsPolicyModalOpen(true);
  };

  const handleEditPolicy = (policy) => {
    setPolicyForm({
      policyName: policy.policyName,
      requiredDailyHours: policy.requiredDailyHours,
      workDays: policy.workDays || []
    });
    setSelectedPolicy(policy);
    setIsPolicyModalOpen(true);
  };

  const handleSavePolicy = async () => {
    try {
      if (selectedPolicy) {
        await policiesAPI.updateWorkingHoursPolicy(selectedPolicy.id, policyForm);
      } else {
        await policiesAPI.createWorkingHoursPolicy(policyForm);
      }
      setIsPolicyModalOpen(false);
      loadData();
      toast.success('Working hours policy saved successfully!');
    } catch (err) {
      toast.error('Failed to save policy: ' + err.message);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    const policy = workingPolicies.find(p => p.id === policyId);
    
    setConfirmConfig({
      title: 'Delete Working Hours Policy',
      message: `Are you sure you want to delete the policy "${policy?.policyName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await policiesAPI.deleteWorkingHoursPolicy(policyId);
          loadData();
          toast.success('Policy deleted successfully!');
        } catch (err) {
          toast.error('Failed to delete policy: ' + err.message);
        }
      }
    });
    setShowConfirm(true);
  };

  // Holiday Functions
  const handleCreateHoliday = () => {
    setHolidayForm({
      holidayName: '',
      holidayDate: '',
      applicableTo: 'Both'
    });
    setSelectedHoliday(null);
    setIsHolidayModalOpen(true);
  };

  const handleEditHoliday = (holiday) => {
    setHolidayForm({
      holidayName: holiday.holidayName,
      holidayDate: holiday.holidayDate,
      applicableTo: holiday.applicableTo
    });
    setSelectedHoliday(holiday);
    setIsHolidayModalOpen(true);
  };

  const handleSaveHoliday = async () => {
    try {
      if (selectedHoliday) {
        await policiesAPI.updateHoliday(selectedHoliday.id, holidayForm);
      } else {
        await policiesAPI.createHoliday(holidayForm);
      }
      setIsHolidayModalOpen(false);
      loadData();
      toast.success('Holiday saved successfully!');
    } catch (err) {
      toast.error('Failed to save holiday: ' + err.message);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    const holiday = holidays.find(h => h.id === holidayId);
    
    setConfirmConfig({
      title: 'Delete Holiday',
      message: `Are you sure you want to delete "${holiday?.holidayName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await policiesAPI.deleteHoliday(holidayId);
          loadData();
          toast.success('Holiday deleted successfully!');
        } catch (err) {
          toast.error('Failed to delete holiday: ' + err.message);
        }
      }
    });
    setShowConfirm(true);
  };

  // Department Functions
  const handleCreateDepartment = () => {
    setDepartmentForm({ 
      name: '', 
      clickupTeamId: '', 
      clickupListId: '',
      orbitId: '', 
      description: '',
      leaveTypeLimits: buildLeaveLimitForm(),
      checkoutRequestLimit: '',
      shouldBlockOverLimit: false,
      overLimitNotice: ''
    });
    setSelectedDepartment(null);
    setIsDepartmentModalOpen(true);
  };

  const handleEditDepartment = (department) => {
    setDepartmentForm({ 
      name: department.name,
      clickupTeamId: department.clickupTeamId || '',
      clickupListId: department.clickupListId || '',
      orbitId: department.orbitId || '',
      description: department.description || '',
      leaveTypeLimits: buildLeaveLimitForm(department.leaveTypeLimits || {}),
      checkoutRequestLimit: department.checkoutRequestLimit === undefined || department.checkoutRequestLimit === null
        ? ''
        : String(department.checkoutRequestLimit),
      shouldBlockOverLimit: Boolean(department.shouldBlockOverLimit),
      overLimitNotice: department.overLimitNotice || ''
    });
    setSelectedDepartment(department);
    setIsDepartmentModalOpen(true);
  };

  const handleSaveDepartment = async () => {
    try {
      if (!departmentForm.name.trim()) {
        toast.warning('Department name is required');
        return;
      }

      // Validate mutual exclusivity
      const hasClickUp = departmentForm.clickupTeamId?.trim();
      const hasOrbit = departmentForm.orbitId?.trim();
      
      if (hasClickUp && hasOrbit) {
        toast.error('Only one integration can be configured. Please choose either ClickUp Team ID or Orbit ID, not both.');
        return;
      }

      const payload = {
        name: departmentForm.name.trim(),
        clickupTeamId: hasClickUp ? departmentForm.clickupTeamId.trim() : null,
        clickupListId: hasClickUp ? (departmentForm.clickupListId?.trim() || null) : null,
        orbitId: hasOrbit ? departmentForm.orbitId.trim() : null,
        description: departmentForm.description?.trim() || null,
        leaveTypeLimits: departmentForm.leaveTypeLimits || {},
        checkoutRequestLimit: departmentForm.checkoutRequestLimit,
        shouldBlockOverLimit: Boolean(departmentForm.shouldBlockOverLimit),
        overLimitNotice: departmentForm.overLimitNotice?.trim() || null
      };

      if (selectedDepartment) {
        // Update/rename department
        await usersAPI.updateDepartment(selectedDepartment.name, { 
          newName: payload.name,
          clickupTeamId: payload.clickupTeamId,
          clickupListId: payload.clickupListId,
          orbitId: payload.orbitId,
          description: payload.description,
          leaveTypeLimits: payload.leaveTypeLimits,
          checkoutRequestLimit: payload.checkoutRequestLimit,
          shouldBlockOverLimit: payload.shouldBlockOverLimit,
          overLimitNotice: payload.overLimitNotice
        });
      } else {
        // Create new department
        await usersAPI.createDepartment(payload);
      }
      setIsDepartmentModalOpen(false);
      loadData();
      toast.success('Department saved successfully!');
    } catch (err) {
      toast.error('Failed to save department: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteDepartment = async (departmentName) => {
    const department = departments.find(d => d.name === departmentName);
    
    setConfirmConfig({
      title: 'Delete Department',
      message: `Are you sure you want to delete "${departmentName}"? This will only work if no users are assigned to it.`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await usersAPI.deleteDepartment(departmentName);
          loadData();
          toast.success('Department deleted successfully!');
        } catch (err) {
          toast.error('Failed to delete department: ' + (err.response?.data?.error || err.message));
        }
      }
    });
    setShowConfirm(true);
  };

  // Push Notification Functions
  const handleSelectDevice = (deviceId) => {
    setSelectedDeviceIds(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  const handleSelectAllDevices = () => {
    const activeDevices = devices.filter(d => d.isActive).map(d => d.id);
    if (selectedDeviceIds.length === activeDevices.length) {
      setSelectedDeviceIds([]);
    } else {
      setSelectedDeviceIds(activeDevices);
    }
  };

  const handleOpenNotificationModal = () => {
    if (selectedDeviceIds.length === 0) {
      toast.warning('Please select at least one device');
      return;
    }
    setNotificationForm({ title: '', body: '' });
    setIsNotificationModalOpen(true);
  };

  const handleSendNotification = async () => {
    try {
      if (!notificationForm.title.trim() || !notificationForm.body.trim()) {
        toast.warning('Title and message are required');
        return;
      }

      const response = await devicesAPI.sendNotification({
        deviceIds: selectedDeviceIds,
        title: notificationForm.title.trim(),
        body: notificationForm.body.trim()
      });

      setIsNotificationModalOpen(false);
      setSelectedDeviceIds([]);
      setNotificationForm({ title: '', body: '' });
      toast.success(`Notification sent! Success: ${response.data.successCount}, Failed: ${response.data.failureCount}`);
    } catch (err) {
      toast.error('Failed to send notification: ' + (err.response?.data?.error || err.message));
    }
  };

  const renderPoliciesTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-lg font-medium text-gray-900">Working Hours Policies</h3>
        <button
          onClick={handleCreatePolicy}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Policy
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner"></div>
        </div>
      ) : workingPolicies.length === 0 ? (
        <div className="text-center py-12">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No policies found</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new working hours policy.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {workingPolicies.map((policy) => (
            <div key={policy.id} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-900">{policy.policyName}</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditPolicy(policy)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePolicy(policy.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-gray-600">
                    <strong>Daily Hours:</strong> {policy.requiredDailyHours}h
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Work Days:</strong> {policy.workDays?.length || 0} days
                  </p>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-1">
                      {(policy.workDays || []).map((day) => (
                        <span
                          key={day}
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800"
                        >
                          {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Policy Modal */}
      {isPolicyModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsPolicyModalOpen(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {selectedPolicy ? 'Edit Policy' : 'Create Policy'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Policy Name</label>
                    <input
                      type="text"
                      value={policyForm.policyName}
                      onChange={(e) => setPolicyForm(prev => ({ ...prev, policyName: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Required Daily Hours</label>
                    <input
                      type="number"
                      step="0.5"
                      min="1"
                      max="24"
                      value={policyForm.requiredDailyHours}
                      onChange={(e) => setPolicyForm(prev => ({ ...prev, requiredDailyHours: parseFloat(e.target.value) }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Days</label>
                    <div className="grid grid-cols-2 gap-2">
                      {weekDays.map((day) => (
                        <label key={day.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={policyForm.workDays.includes(day.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPolicyForm(prev => ({
                                  ...prev,
                                  workDays: [...prev.workDays, day.id]
                                }));
                              } else {
                                setPolicyForm(prev => ({
                                  ...prev,
                                  workDays: prev.workDays.filter(d => d !== day.id)
                                }));
                              }
                            }}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-900">{day.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSavePolicy}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsPolicyModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderHolidaysTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-lg font-medium text-gray-900">Holidays</h3>
        <button
          onClick={handleCreateHoliday}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Holiday
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner"></div>
        </div>
      ) : holidays.length === 0 ? (
        <div className="text-center py-12">
          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No holidays found</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding a new holiday.</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {holidays.map((holiday) => (
              <li key={holiday.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">{holiday.holidayName}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(holiday.holidayDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        holiday.applicableTo === 'Both' 
                          ? 'bg-blue-100 text-blue-800'
                          : holiday.applicableTo === 'Full-Time'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {holiday.applicableTo}
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditHoliday(holiday)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Holiday Modal */}
      {isHolidayModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsHolidayModalOpen(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {selectedHoliday ? 'Edit Holiday' : 'Add Holiday'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Holiday Name</label>
                    <input
                      type="text"
                      value={holidayForm.holidayName}
                      onChange={(e) => setHolidayForm(prev => ({ ...prev, holidayName: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date</label>
                    <input
                      type="date"
                      value={holidayForm.holidayDate}
                      onChange={(e) => setHolidayForm(prev => ({ ...prev, holidayDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Applicable To</label>
                    <select
                      value={holidayForm.applicableTo}
                      onChange={(e) => setHolidayForm(prev => ({ ...prev, applicableTo: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="Both">Both</option>
                      <option value="Full-Time">Full-Time Only</option>
                      <option value="Intern">Intern Only</option>
                    </select>
                  </div>

                  {/* Working Hours Policies Display */}
                  {workingPolicies && workingPolicies.length > 0 && (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Affected Working Hours Policies
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        This holiday will apply to all working hours policies. Listed below are the current policies:
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {workingPolicies.map((policy) => (
                          <div key={policy.id} className="bg-gray-50 rounded-md p-3 border border-gray-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <ClockIcon className="h-4 w-4 text-gray-500" />
                                  <span className="text-sm font-medium text-gray-900">{policy.policyName}</span>
                                </div>
                                <div className="mt-1 text-xs text-gray-600 ml-6">
                                  <span>{policy.requiredDailyHours} hours/day</span>
                                  {policy.workDays && policy.workDays.length > 0 && (
                                    <span className="ml-2">
                                      • {policy.workDays.map((day) => day.charAt(0).toUpperCase() + day.slice(1).substring(0, 3)).join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSaveHoliday}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsHolidayModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDepartmentsTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-lg font-medium text-gray-900">Departments</h3>
        <button
          onClick={handleCreateDepartment}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Department
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner"></div>
        </div>
      ) : !departments || departments.length === 0 ? (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No departments found</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding a new department.</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {(departments || []).map((department) => (
              <li key={department.id || department.name}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="flex-shrink-0">
                        <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{department.name}</p>
                          {department.clickupTeamId && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              ClickUp
                            </span>
                          )}
                          {department.orbitId && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Orbit
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-sm text-gray-500">
                            {department.userCount} {department.userCount === 1 ? 'user' : 'users'}
                          </p>
                          {department.clickupTeamId && (
                            <p className="text-xs text-blue-600">
                              ClickUp Team: {department.clickupTeamId}
                            </p>
                          )}
                          {department.clickupListId && (
                            <p className="text-xs text-blue-600">
                              ClickUp List: {department.clickupListId}
                            </p>
                          )}
                          {department.orbitId && (
                            <p className="text-xs text-purple-600">
                              Orbit ID: {department.orbitId}
                            </p>
                          )}
                        </div>
                        {department.description && (
                          <p className="text-xs text-gray-500 mt-1">{department.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditDepartment(department)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit department"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDepartment(department.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete department"
                        disabled={department.userCount > 0}
                      >
                        <TrashIcon className={`h-4 w-4 ${department.userCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Department Modal */}
      {isDepartmentModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsDepartmentModalOpen(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {selectedDepartment ? 'Edit Department' : 'Add Department'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Department Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={departmentForm.name}
                      onChange={(e) => setDepartmentForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Engineering, Marketing, Sales"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      ClickUp Team/Workspace ID <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={departmentForm.clickupTeamId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDepartmentForm(prev => ({ 
                          ...prev, 
                          clickupTeamId: value,
                          // Clear Orbit ID if ClickUp is being set
                          orbitId: value.trim() ? '' : prev.orbitId
                        }));
                      }}
                      placeholder="e.g., 37428735"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      disabled={!!departmentForm.orbitId?.trim()}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the ClickUp workspace ID to enable task tracking for this department.
                      {departmentForm.orbitId?.trim() && (
                        <span className="text-orange-600 block mt-1">
                          ⚠️ Orbit ID is set. Clear Orbit ID first to use ClickUp.
                        </span>
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      ClickUp List ID
                    </label>
                    <input
                      type="text"
                      value={departmentForm.clickupListId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDepartmentForm(prev => ({
                          ...prev,
                          clickupListId: value
                        }));
                      }}
                      placeholder="e.g., 901203948576"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      disabled={!!departmentForm.orbitId?.trim()}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Configure the default ClickUp list used when EMS creates a ClickUp task for this department.
                    </p>
                    {departmentForm.clickupTeamId?.trim() && !departmentForm.clickupListId?.trim() && !departmentForm.orbitId?.trim() && (
                      <p className="mt-1 text-xs text-amber-600">
                        Add a ClickUp List ID to enable "Create in ClickUp" from the EMS task modal.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Orbit ID <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={departmentForm.orbitId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDepartmentForm(prev => ({ 
                          ...prev, 
                          orbitId: value,
                          // Clear ClickUp ID if Orbit is being set
                          clickupTeamId: value.trim() ? '' : prev.clickupTeamId
                        }));
                      }}
                      placeholder="e.g., orbit-123"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      disabled={!!departmentForm.clickupTeamId?.trim()}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the Orbit ID to enable Orbit API task tracking for this department.
                      {departmentForm.clickupTeamId?.trim() && (
                        <span className="text-orange-600 block mt-1">
                          ⚠️ ClickUp ID is set. Clear ClickUp ID first to use Orbit.
                        </span>
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <textarea
                      value={departmentForm.description}
                      onChange={(e) => setDepartmentForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this department..."
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900">Annual Leave Limits</h4>
                    <p className="text-xs text-gray-500 mt-1">
                      Approved-only, counted fully in the start month of the year. Leave blank for unlimited.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {leaveLimitTypes.map((leaveType) => {
                        const isShortLeave = leaveType === 'Short Leave';
                        return (
                          <div key={leaveType}>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {leaveType}
                            </label>
                            {isShortLeave ? (
                              <div className="mt-1 block w-full rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                                Exempt from limits
                              </div>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={departmentForm.leaveTypeLimits?.[leaveType] ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setDepartmentForm(prev => ({
                                    ...prev,
                                    leaveTypeLimits: {
                                      ...(prev.leaveTypeLimits || {}),
                                      [leaveType]: value
                                    }
                                  }));
                                }}
                                placeholder="Unlimited"
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Monthly Checkout Request Limit <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={departmentForm.checkoutRequestLimit}
                      onChange={(e) => setDepartmentForm(prev => ({ ...prev, checkoutRequestLimit: e.target.value }))}
                      placeholder="Unlimited"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Approved-only, counted in the request month.</p>
                  </div>

                  <div className="flex items-start gap-3 border-t border-gray-200 pt-4">
                    <input
                      type="checkbox"
                      checked={departmentForm.shouldBlockOverLimit}
                      onChange={(e) => setDepartmentForm(prev => ({ ...prev, shouldBlockOverLimit: e.target.checked }))}
                      className="h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Block submissions when limits are exceeded</p>
                      <p className="text-xs text-gray-500">If unchecked, users will see a warning but can still submit.</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Over-limit Notice <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <textarea
                      value={departmentForm.overLimitNotice}
                      onChange={(e) => setDepartmentForm(prev => ({ ...prev, overLimitNotice: e.target.value }))}
                      placeholder="Message shown when a user exceeds limits (department-specific)."
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  {selectedDepartment && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> Updating this department will affect all {selectedDepartment.userCount} user(s) assigned to it.
                      </p>
                    </div>
                  )}

                  {departmentForm.clickupTeamId && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700">
                            ClickUp integration will be enabled for this department. Task counts will appear in the Manager Dashboard.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {departmentForm.orbitId && (
                    <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded-r">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-purple-700">
                            Orbit integration will be enabled for this department. Task counts will appear in the Manager Dashboard.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSaveDepartment}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {selectedDepartment ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={() => setIsDepartmentModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPushNotificationsTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Push Notification Devices</h3>
          <p className="text-sm text-gray-500 mt-1">View all registered devices and send push notifications</p>
        </div>
        <button
          onClick={handleOpenNotificationModal}
          disabled={selectedDeviceIds.length === 0}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <PaperAirplaneIcon className="h-4 w-4 mr-2" />
          Send Notification ({selectedDeviceIds.length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner"></div>
        </div>
      ) : devices.length === 0 ? (
        <div className="text-center py-12">
          <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No devices found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No devices have been registered yet. Devices will appear here when users register their mobile apps.
          </p>
          <p className="mt-2 text-xs text-gray-400">
            Users need to install the mobile app and grant push notification permissions for devices to appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All */}
          <div className="bg-white shadow rounded-lg p-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedDeviceIds.length > 0 && selectedDeviceIds.length === devices.filter(d => d.isActive).length}
                onChange={handleSelectAllDevices}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-900">
                Select All Active Devices ({devices.filter(d => d.isActive).length})
              </span>
            </label>
          </div>

          {/* Devices List */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {devices.map((device) => (
                <li key={device.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedDeviceIds.includes(device.id)}
                        onChange={() => handleSelectDevice(device.id)}
                        disabled={!device.isActive}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">
                                {device.user ? `${device.user.firstName} ${device.user.lastName}` : 'Unknown User'}
                              </p>
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                device.platform === 'ios' 
                                  ? 'bg-blue-100 text-blue-800'
                                  : device.platform === 'android'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {device.platform.toUpperCase()}
                              </span>
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                device.isActive 
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {device.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="mt-1 text-sm text-gray-500">
                              {device.user && (
                                <>
                                  <span>{device.user.officialEmail}</span>
                                  {device.user.Department && (
                                    <span className="ml-2">• {device.user.Department}</span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="mt-2">
                              <p className="text-xs font-mono text-gray-600 break-all">
                                Token: {device.token ? `${device.token.substring(0, 50)}...` : 'N/A'}
                              </p>
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              Last used: {device.lastUsedAt 
                                ? new Date(device.lastUsedAt).toLocaleString()
                                : 'Never'}
                              {' • '}
                              Registered: {device.createdAt 
                                ? new Date(device.createdAt).toLocaleDateString()
                                : 'Unknown'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {isNotificationModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsNotificationModalOpen(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Send Push Notification
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Sending to {selectedDeviceIds.length} device(s)
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title *</label>
                    <input
                      type="text"
                      value={notificationForm.title}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Notification title"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message *</label>
                    <textarea
                      value={notificationForm.body}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, body: e.target.value }))}
                      placeholder="Notification message"
                      rows={4}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {notificationForm.body.length}/500 characters
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSendNotification}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Send Notification
                </button>
                <button
                  onClick={() => setIsNotificationModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderWebhookTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Webhook Settings</h3>
      
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Webhook Configuration</h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>Configure webhook URLs to receive real-time attendance notifications.</p>
          </div>
          
          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Webhook URL</label>
              <input
                type="url"
                placeholder="https://your-webhook-url.com/attendance"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Secret Key (Optional)</label>
              <input
                type="password"
                placeholder="Enter webhook secret for verification"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Enable webhook notifications
              </label>
            </div>
          </div>

          <div className="mt-5">
            <button className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700">
              Save Webhook Settings
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Webhook Events</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <code>attendance_logged</code> - Triggered when an attendance log is created</li>
          <li>• <code>daily_attendance_report</code> - Daily summary report</li>
          <li>• <code>user_status_changed</code> - When user status is updated</li>
        </ul>
      </div>
    </div>
  );

  const renderAttendanceAccessTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Attendance Access</h3>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Web Attendance</h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>Control whether employees can use check-in and check-out from the web app.</p>
          </div>

          <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={attendanceAccess.allowWebAttendance}
                onChange={(e) => setAttendanceAccess({
                  allowWebAttendance: e.target.checked
                })}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <span className="block text-sm font-medium text-gray-900">
                  Allow web attendance
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  When off, check-in and check-out are available only from the mobile app.
                </span>
              </div>
            </label>
          </div>

          <div className="mt-5">
            <button
              onClick={handleSaveAttendanceAccess}
              disabled={attendanceAccessSaving}
              className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {attendanceAccessSaving ? 'Saving...' : 'Save Attendance Access'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen overflow-x-hidden px-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage system configurations and policies</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto sticky top-0 z-20 bg-gray-50/80 backdrop-blur">
        <nav className="-mb-px flex space-x-2 sm:space-x-8 min-w-full">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-3 px-3 sm:px-4 border-b-2 font-medium text-sm flex items-center rounded-t-md`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6 grid grid-cols-1">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="text-red-800">Error: {error}</div>
          </div>
        )}

        {activeTab === 'policies' && renderPoliciesTab()}
        {activeTab === 'holidays' && renderHolidaysTab()}
        {activeTab === 'departments' && renderDepartmentsTab()}
        {activeTab === 'attendance-access' && renderAttendanceAccessTab()}
        {activeTab === 'push-notifications' && renderPushNotificationsTab()}
        {activeTab === 'webhook' && renderWebhookTab()}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText || 'Confirm'}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
      />
    </div>
  );
}

export default Settings;
